/**
 * ReadOnlyLayoutView: a static SVG rendering of a layout's object graph.
 * Used by BrideShareView (pretty mode) and SetupSheetDocument (the
 * printable export) — anywhere a layout needs to be SHOWN but never EDITED.
 *
 * COORDINATE SYSTEM — fix for setup-sheet position drift:
 *
 * The editor canvas uses FEET_TO_PX = 12 (canvasConstants.ts). The previous
 * production version of this file used FEET_TO_PX = 10, labeled "overview
 * scale". That caused every object to appear at 83% of its editor position
 * (e.g. a table at x=10ft showed at 120px in editor, 100px on sheet).
 * FIX: This file now uses FEET_TO_PX = 12 to match the editor exactly.
 *
 * Additionally, the dance floor had a coordinate ORIGIN bug:
 * - Editor (DanceFloor.tsx): Group at x=object.x*FTP, y=object.y*FTP,
 *   then Rect draws from (0,0) — so object.x/y is the TOP-LEFT corner.
 * - Old SVG renderer: translate(cx,cy) then rect x=-w/2, y=-h/2 — CENTERED.
 * FIX: Dance floor SVG now uses rect x=0, y=0 (top-left origin).
 *
 * All other shapes confirmed correct (center origin in both editor and SVG):
 * - round_table: Circle at 0,0 → center ✓
 * - rect tables, head_table, sweetheart: Rect x=-w/2, y=-h/2 → center ✓
 * - feature zones: Rect x=-w/2, y=-h/2 → center ✓
 * - text_note: Text with offsetX/offsetY centering → center ✓
 * - standalone_chair: Circle at 0,0 → center ✓
 * - arbor: Rect x=-w/2, y=-h/2 → center ✓
 */

import type { LayoutObject, ObjectCatalogEntry, RoomDefinition } from '../../domain/types';

/**
 * Must match src/components/canvas/canvasConstants.ts FEET_TO_PX.
 * If canvasConstants.ts changes, change this too.
 */
const FEET_TO_PX = 12;

const FEATURE_ICONS: Record<string, string> = {
  cake_table: '\u{1F382}',
  gift_table: '\u{1F381}',
  dessert_station: '\u{1F370}',
  donut_wall: '\u{1F369}',
  photo_booth: '\u{1F4F8}',
  dj_booth: '\u{1F3A7}',
  cocktail_table: '\u{1F377}',
  arbor: '\u{1F33F}',
};

interface ReadOnlyLayoutViewProps {
  room: RoomDefinition;
  objects: LayoutObject[];
  catalog: ObjectCatalogEntry[];
  showNumbers?: boolean;
}

export function ReadOnlyLayoutView({ room, objects, catalog, showNumbers = false }: ReadOnlyLayoutViewProps) {
  const widthPx = room.widthFt * FEET_TO_PX;
  const heightPx = room.lengthFt * FEET_TO_PX;

  function entryFor(type: string) {
    return catalog.find((c) => c.itemType === type);
  }

  const tableTypes = new Set(['round_table', 'rect_table_6', 'rect_table_8']);
  let tableCounter = 0;
  const numberedIds = new Map<string, number>();
  if (showNumbers) {
    for (const obj of objects) {
      if (tableTypes.has(obj.type)) {
        tableCounter += 1;
        numberedIds.set(obj.id, tableCounter);
      }
    }
  }

  return (
    <svg
      viewBox={`0 0 ${widthPx} ${heightPx}`}
      width={widthPx}
      height={heightPx}
      className="mx-auto"
      role="img"
      aria-label={`Floor plan of ${room.displayName}`}
    >
      <rect x={0} y={0} width={widthPx} height={heightPx} fill="#FBF8F2" stroke="#C9A35C" strokeWidth={2} />

      <g transform={`translate(${room.entrance.x * FEET_TO_PX}, ${room.entrance.y * FEET_TO_PX})`}>
        <line x1={-15} y1={0} x2={15} y2={0} stroke="#6E8569" strokeWidth={3} strokeLinecap="round" />
        <text x={0} y={14} fontSize={8} fill="#6E8569" textAnchor="middle" fontFamily="Inter, sans-serif">
          ENTRANCE
        </text>
      </g>

      {room.exclusionZones.map((zone, i) => {
        const zoneWidthPx = zone.width * FEET_TO_PX;
        const zoneHeightPx = zone.height * FEET_TO_PX;
        const isTallAndNarrow = zoneHeightPx > zoneWidthPx * 2;
        return (
          <g key={i} transform={`translate(${zone.x * FEET_TO_PX}, ${zone.y * FEET_TO_PX}) rotate(${zone.rotation})`}>
            <rect width={zoneWidthPx} height={zoneHeightPx} fill="#E9EEE8" stroke="#6E8569" strokeDasharray="3,3" />
            {isTallAndNarrow ? (
              <text transform={`translate(${zoneWidthPx / 2}, ${zoneHeightPx / 2}) rotate(90)`} fontSize={7} textAnchor="middle" fontFamily="Inter, sans-serif" fill="#6E8569">
                {zone.label}
              </text>
            ) : (
              <text x={zoneWidthPx / 2} y={zoneHeightPx / 2 + 2.5} fontSize={7} textAnchor="middle" fontFamily="Inter, sans-serif" fill="#6E8569">
                {zone.label}
              </text>
            )}
          </g>
        );
      })}

      {objects
        .slice()
        .sort((a, b) => a.zIndex - b.zIndex)
        .map((obj) => {
          const entry = entryFor(obj.type);
          if (!entry) return null;
          const tableNum = numberedIds.get(obj.id);
          return <ObjectGlyph key={obj.id} object={obj} entry={entry} tableNum={tableNum} />;
        })}
    </svg>
  );
}

interface ObjectGlyphProps {
  key?: string;
  object: LayoutObject;
  entry: ObjectCatalogEntry;
  tableNum?: number;
}

function ObjectGlyph({
  object,
  entry,
  tableNum,
}: ObjectGlyphProps) {
  // cx/cy is the center point for center-origin objects,
  // or top-left for top-left-origin objects (dance floor only).
  const cx = object.x * FEET_TO_PX;
  const cy = object.y * FEET_TO_PX;
  const label = tableNum !== undefined ? String(tableNum) : object.label;

  if (object.type === 'round_table') {
    // CENTER origin — confirmed: editor Circle at 0,0 within Group
    const tableRadiusPx = 2.5 * FEET_TO_PX;
    const chairCount = object.seats ?? 8;
    const chairRingRadius = tableRadiusPx + 10;
    return (
      <g transform={`translate(${cx}, ${cy}) rotate(${object.rotation})`}>
        {Array.from({ length: chairCount }).map((_, i) => {
          const angle = (i / chairCount) * Math.PI * 2 - Math.PI / 2;
          return (
            <circle key={i}
              cx={Math.cos(angle) * chairRingRadius}
              cy={Math.sin(angle) * chairRingRadius}
              r={5} fill="#FBF8F2" stroke="#C9A35C" strokeWidth={0.8}
            />
          );
        })}
        <circle r={tableRadiusPx} fill="#FFFEFC" stroke="#8FA38B" strokeWidth={1} />
        <text y={4} fontSize={tableNum !== undefined ? 11 : 8} fontWeight={tableNum !== undefined ? 'bold' : 'normal'} textAnchor="middle" fontFamily="Inter, sans-serif" fill="#4D453C">
          {label}
        </text>
      </g>
    );
  }

  if (object.type === 'rect_table_6' || object.type === 'rect_table_8' || object.type === 'head_table' || object.type === 'sweetheart_table') {
    // CENTER origin — confirmed: editor Rect x=-w/2, y=-h/2 within Group
    const w = entry.defaultWidthFt * FEET_TO_PX;
    const h = entry.defaultHeightFt * FEET_TO_PX;
    return (
      <g transform={`translate(${cx}, ${cy}) rotate(${object.rotation})`}>
        <rect x={-w / 2} y={-h / 2} width={w} height={h} fill="#FFFEFC" stroke="#8FA38B" strokeWidth={1} />
        <text y={4} fontSize={tableNum !== undefined ? 11 : 8} fontWeight={tableNum !== undefined ? 'bold' : 'normal'} textAnchor="middle" fontFamily="Inter, sans-serif" fill="#4D453C">
          {label}
        </text>
      </g>
    );
  }

  if (object.type === 'dance_floor_15' || object.type === 'dance_floor_18') {
    // TOP-LEFT origin — confirmed: editor Group at x*FTP,y*FTP then Rect at 0,0
    // FIXED: was incorrectly using center origin (rect x=-w/2) in production
    const w = entry.defaultWidthFt * FEET_TO_PX;
    const h = entry.defaultHeightFt * FEET_TO_PX;
    return (
      <g transform={`translate(${cx}, ${cy}) rotate(${object.rotation})`}>
        <rect x={0} y={0} width={w} height={h} fill="#F3E6C8" stroke="#B08A42" strokeWidth={1} />
        <text x={w / 2} y={h / 2 + 4} fontSize={9} textAnchor="middle" fontFamily="Fraunces, serif" fill="#6B5227">
          {object.label}
        </text>
      </g>
    );
  }

  if (object.type === 'standalone_chair') {
    // CENTER origin — new type, circle same visual as table-attached chairs
    // Radius = defaultWidthFt * FEET_TO_PX / 2 (NOT the full width — audit note)
    const r = (entry.defaultWidthFt * FEET_TO_PX) / 2;
    return (
      <g transform={`translate(${cx}, ${cy}) rotate(${object.rotation})`}>
        <circle r={r} fill="#FBF8F2" stroke="#C9A35C" strokeWidth={1.2} />
      </g>
    );
  }

  if (object.type === 'arbor') {
    // CENTER origin — new type, dashed rectangle
    const w = entry.defaultWidthFt * FEET_TO_PX;
    const h = entry.defaultHeightFt * FEET_TO_PX;
    return (
      <g transform={`translate(${cx}, ${cy}) rotate(${object.rotation})`}>
        <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={3} fill="#EEF2EF" stroke="#5F8767" strokeWidth={1.5} strokeDasharray="4,2" />
        <text x={0} y={h / 2 - 3} fontSize={7} textAnchor="middle" fontFamily="Inter, sans-serif" fill="#3D5F44">
          {object.label}
        </text>
      </g>
    );
  }

  if (object.type === 'text_note') {
    // CENTER origin — confirmed: editor Text with offsetX/offsetY centering within Group
    return (
      <g transform={`translate(${cx}, ${cy}) rotate(${object.rotation})`}>
        <text y={3} fontSize={9} fontStyle="italic" textAnchor="middle" fontFamily="Inter, sans-serif" fill="#4D453C">
          {object.label}
        </text>
      </g>
    );
  }

  // Feature zones (cake/gift/dessert/donut/photo booth/DJ/cocktail) — CENTER origin
  const w = entry.defaultWidthFt * FEET_TO_PX;
  const h = entry.defaultHeightFt * FEET_TO_PX;
  const icon = FEATURE_ICONS[object.type] ?? '';
  const isRound = object.type === 'cocktail_table' || object.type === 'cake_table';

  if (isRound) {
    const r = Math.min(w, h) / 2;
    return (
      <g transform={`translate(${cx}, ${cy}) rotate(${object.rotation})`}>
        <circle r={r} fill="#FFFEFC" stroke="#8FA38B" strokeWidth={1} />
        <text y={1} fontSize={Math.min(w, h) * 0.4} textAnchor="middle">{icon}</text>
        <text y={h / 2 - 4} fontSize={7} textAnchor="middle" fontFamily="Inter, sans-serif" fill="#6B6258">{object.label}</text>
      </g>
    );
  }

  return (
    <g transform={`translate(${cx}, ${cy}) rotate(${object.rotation})`}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={3} fill="#FFFEFC" stroke="#8FA38B" strokeWidth={1} />
      <text y={1} fontSize={Math.min(w, h) * 0.4} textAnchor="middle">{icon}</text>
      <text y={h / 2 - 4} fontSize={7} textAnchor="middle" fontFamily="Inter, sans-serif" fill="#6B6258">{object.label}</text>
    </g>
  );
}
